import React, { useState, useEffect, useCallback } from 'react';
import { MdAdd, MdSearch, MdMeetingRoom, MdEdit, MdDelete, MdClose, MdBusiness, MdPeople, MdSquareFoot } from 'react-icons/md';
import API from '../api';
import { useAuth } from '../hooks/useAuth';
import PublicLinkCard from '../components/PublicLinkCard';
import { translateUiText, useLanguage } from '../i18n';
import { useSiteSettings, formatMoney } from '../hooks/useSiteSettings';

const SPACE_TYPES = [
    'office', 'conference_room', 'training_room', 'stock_room',
    'server_room', 'bathroom', 'parking', 'common_area', 'lab', 'other'
];
const SPACE_TYPE_LABELS = {
    office: { key: 'spaces.typeOffice', fallback: 'Office' },
    conference_room: { key: 'spaces.typeConferenceRoom', fallback: 'Conference Room' },
    training_room: { key: 'spaces.typeTrainingRoom', fallback: 'Training Room' },
    stock_room: { key: 'spaces.typeStockRoom', fallback: 'Stock Room' },
    server_room: { key: 'spaces.typeServerRoom', fallback: 'Server Room' },
    bathroom: { key: 'spaces.typeBathroom', fallback: 'Bathroom' },
    parking: { key: 'spaces.typeParking', fallback: 'Parking' },
    common_area: { key: 'spaces.typeCommonArea', fallback: 'Common Area' },
    lab: { key: 'spaces.typeLab', fallback: 'Lab' },
    other: { key: 'spaces.typeOther', fallback: 'Other' },
};
const SPACE_STATUS = ['available', 'occupied', 'maintenance', 'reserved'];
const SPACE_STATUS_LABELS = {
    available: { key: 'spaces.statusAvailable', fallback: 'Available' },
    occupied: { key: 'spaces.statusOccupied', fallback: 'Occupied' },
    maintenance: { key: 'spaces.statusMaintenance', fallback: 'Under Maintenance' },
    reserved: { key: 'spaces.statusReserved', fallback: 'Reserved' },
};

function Modal({ title, onClose, children }) {
    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '16px 20px', borderBottom: '1px solid #e2e8f0' }}>
                    <h6 style={{ margin: 0, fontWeight: 600 }}>{title}</h6>
                    <button onClick={onClose} style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#64748b', padding: 4 }}><MdClose size={20} /></button>
                </div>
                <div style={{ padding: 20 }}>{children}</div>
            </div>
        </div>
    );
}

const emptySpaceForm = { building_id: '', name: '', floor: '', room_number: '', space_type: 'office', capacity: '', area: '', price_per_hour: 0, price_per_day: 0, price_per_month: 0, status: 'available', assigned_to: '', department: '', amenities: '' };
const emptyBuildingForm = { name: '', address: '', floors: 1, total_area: '', manager_id: '' };

export default function Spaces() {
    const { isAdmin, canManageSpace, isManager, assignedFacility } = useAuth();
    const { currency } = useSiteSettings();
    const { t } = useLanguage();
    const RWF = n => formatMoney(n, currency);
    const typeLabel = type => {
        const m = SPACE_TYPE_LABELS[type];
        return m ? t(m.key, m.fallback) : type;
    };
    const statusLabel = status => {
        const m = SPACE_STATUS_LABELS[status];
        return m ? t(m.key, m.fallback) : status;
    };
    const [spaces, setSpaces] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [filterBuilding, setFilterBuilding] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [showSpaceModal, setShowSpaceModal] = useState(false);
    const [showBuildingModal, setShowBuildingModal] = useState(false);
    const [showBookingModal, setShowBookingModal] = useState(false);
    const [spaceForm, setSpaceForm] = useState(emptySpaceForm);
    const [buildingForm, setBuildingForm] = useState(emptyBuildingForm);
    const [bookingForm, setBookingForm] = useState({ space_id: '', title: '', start_datetime: '', end_datetime: '', attendees: 1, purpose: '', order_type: 'booking', rental_months: 1, pricing_unit: 'hour' });
    const [bookingSpace, setBookingSpace] = useState(null);
    const [depositAccepted, setDepositAccepted] = useState(false);
    const [editSpaceId, setEditSpaceId] = useState(null);
    const [editBuildingId, setEditBuildingId] = useState(null);
    const [saving, setSaving] = useState(false);
    const [stats, setStats] = useState(null);
    const [activeTab, setActiveTab] = useState('spaces');

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = {};
            if (search) params.search = search;
            if (filterBuilding) params.building_id = filterBuilding;
            if (filterStatus) params.status = filterStatus;
            const [spacesRes, buildingsRes, statsRes] = await Promise.all([
                API.get('/spaces', { params }),
                API.get('/spaces/buildings'),
                API.get('/spaces/stats')
            ]);
            setSpaces(spacesRes.data);
            setBuildings(buildingsRes.data);
            setStats(statsRes.data);
        } finally { setLoading(false); }
    }, [search, filterBuilding, filterStatus]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { API.get('/auth/users/list').then(r => setUsers(r.data.filter(u => u.role !== 'developer'))); }, []);

    // Pre-filter to manager's facility on first load
    useEffect(() => {
        if (isManager && assignedFacility?.id && !filterBuilding) {
            setFilterBuilding(String(assignedFacility.id));
        }
    }, [isManager, assignedFacility]); // eslint-disable-line react-hooks/exhaustive-deps

    const openCreateSpace = () => { setSpaceForm(emptySpaceForm); setEditSpaceId(null); setShowSpaceModal(true); };
    const openEditSpace = s => { setSpaceForm({ ...emptySpaceForm, ...s }); setEditSpaceId(s.id); setShowSpaceModal(true); };
    const openEditBuilding = building => {
        setBuildingForm({ ...emptyBuildingForm, ...building });
        setEditBuildingId(building.id);
        setShowBuildingModal(true);
    };
    const openBooking = s => {
        setBookingForm({ space_id: s.id, title: '', start_datetime: '', end_datetime: '', attendees: 1, purpose: '', order_type: 'booking', rental_months: 1, pricing_unit: 'hour' });
        setBookingSpace(s);
        setDepositAccepted(false);
        setShowBookingModal(true);
    };

    const handleSaveSpace = async e => {
        e.preventDefault(); setSaving(true);
        try {
            if (editSpaceId) await API.put(`/spaces/${editSpaceId}`, spaceForm);
            else await API.post('/spaces', spaceForm);
            setShowSpaceModal(false);
            window.dispatchEvent(new Event('fms:data-changed'));
            localStorage.setItem('fms_data_changed', String(Date.now()));
            load();
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); } finally { setSaving(false); }
    };

    const handleSaveBuilding = async e => {
        e.preventDefault(); setSaving(true);
        try {
            if (editBuildingId) await API.put(`/spaces/buildings/${editBuildingId}`, buildingForm);
            else await API.post('/spaces/buildings', buildingForm);
            setShowBuildingModal(false); load();
            setEditBuildingId(null);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); } finally { setSaving(false); }
    };

    const handleDeleteSpace = async id => {
        if (!window.confirm(translateUiText('Permanently delete this space? Its bookings will also be deleted. This cannot be undone.'))) return;
        try {
            await API.delete(`/spaces/${id}`);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Delete failed'));
        }
    };

    const handleDeleteBuilding = async id => {
        if (!window.confirm(translateUiText('Deactivate this facility?'))) return;
        try {
            await API.delete(`/spaces/buildings/${id}`);
            load();
        } catch (err) {
            alert(err.response?.data?.error || translateUiText('Delete failed'));
        }
    };

    const handleSaveBooking = async e => {
        e.preventDefault(); setSaving(true);
        try {
            const { data } = await API.post('/spaces/bookings', { ...bookingForm, deposit_accepted: depositAccepted });
            setShowBookingModal(false); load();
            if (data?.deposit_amount > 0) alert(data.message);
        } catch (err) { alert(err.response?.data?.error || translateUiText('Save failed')); } finally { setSaving(false); }
    };

    const typeIcon = type => {
        const icons = {
            conference_room: '🏛️', office: '🏢', lab: '🔬', stock_room: '📦',
            common_area: '☕', bathroom: '🚻', parking: '🅿️', other: '🏠',
            training_room: '📚', server_room: '🖥️'
        };
        return icons[type] || '🏠';
    };

    const bookingHours = bookingForm.start_datetime && bookingForm.end_datetime
        ? Math.max(0, (new Date(bookingForm.end_datetime) - new Date(bookingForm.start_datetime)) / 3600000)
        : 0;
    const bookingUnitPrice = bookingSpace ? Number(bookingSpace[`price_per_${bookingForm.pricing_unit}`] || 0) : 0;
    const bookingQuantity = bookingForm.pricing_unit === 'hour' ? bookingHours
        : bookingForm.pricing_unit === 'day' ? Math.max(1, bookingHours / 24)
        : Math.max(1, Number(bookingForm.rental_months || 1));
    const bookingTotal = bookingHours > 0 ? Math.ceil(bookingQuantity * bookingUnitPrice) : 0;
    const bookingDepositPercent = Number(stats?.deposit_percent || 0) || 20;
    const bookingDepositAmount = Math.ceil(bookingTotal * bookingDepositPercent / 100);

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
                <div>
                    <h4 style={{ margin: 0, fontWeight: 700 }}>{t('spaces.pageTitle', 'Space Management')}</h4>
                    <p style={{ margin: 0, color: '#64748b', fontSize: 13 }}>{t('spaces.subtitle', 'Room allocation, occupancy & bookings')}</p>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                    {isAdmin && (
                        <button className="btn btn-light btn-sm" onClick={() => { setBuildingForm(emptyBuildingForm); setEditBuildingId(null); setShowBuildingModal(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdBusiness size={16} /> {t('spaces.addFacility', 'Add Facility')}
                        </button>
                    )}
                    {canManageSpace && (
                        <button className="btn btn-primary btn-sm" onClick={openCreateSpace} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <MdAdd size={16} /> {t('spaces.addSpace', 'Add Space')}
                        </button>
                    )}
                </div>
            </div>

            {isAdmin && <PublicLinkCard availableSpaces={stats ? (stats.by_status?.find(s => s.status === 'available')?.cnt || 0) : undefined} />}

            {/* Stats */}
            {stats && (
                <div className="row g-3 mb-4">
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon blue"><MdMeetingRoom /></div>
                            <div><div className="stat-value">{stats.total}</div><div className="stat-label">{t('spaces.statTotal', 'Total Spaces')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon green"><MdMeetingRoom /></div>
                            <div><div className="stat-value">{stats.by_status?.find(s => s.status === 'available')?.cnt || 0}</div><div className="stat-label">{t('spaces.statAvailable', 'Available')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon yellow"><MdPeople /></div>
                            <div><div className="stat-value">{stats.by_status?.find(s => s.status === 'occupied')?.cnt || 0}</div><div className="stat-label">{t('spaces.statOccupied', 'Occupied')}</div></div>
                        </div>
                    </div>
                    <div className="col-md-3 col-6">
                        <div className="stat-card">
                            <div className="stat-icon teal"><MdSquareFoot /></div>
                            <div><div className="stat-value">{stats.occupancy_rate || 0}%</div><div className="stat-label">{t('spaces.statOccupancyRate', 'Occupancy Rate')}</div></div>
                        </div>
                    </div>
                </div>
            )
            }

            {/* Tabs */}
            <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderBottom: '1px solid #e2e8f0' }}>
                {['spaces', 'buildings'].map(tab => (
                    <button key={tab} onClick={() => setActiveTab(tab)} style={{
                        padding: '10px 20px', background: 'none', border: 'none', cursor: 'pointer',
                        fontWeight: 500, fontSize: 14,
                        color: activeTab === tab ? '#0ea5e9' : '#64748b',
                        borderBottom: activeTab === tab ? '2px solid #0ea5e9' : '2px solid transparent',
                        textTransform: 'capitalize'
                    }}>{tab === 'spaces' ? t('spaces.tabSpaces', 'Spaces') : t('spaces.tabBuildings', 'Buildings')}</button>
                ))}
            </div>

            {
                activeTab === 'spaces' && (
                    <>
                        {/* Manager facility banner */}
                        {isManager && assignedFacility && (
                            <div style={{ background: '#e0f2fe', border: '1px solid #bae6fd', borderRadius: 10, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 10 }}>
                                <MdBusiness style={{ color: '#0284c7', fontSize: 20, flexShrink: 0 }} />
                                <div>
                                    <span style={{ fontWeight: 600, color: '#0369a1', fontSize: 13 }}>{t('spaces.yourFacilityLabel', 'Your Facility: ')}{assignedFacility.name}</span>
                                    <span style={{ fontSize: 12, color: '#0284c7', marginLeft: 8 }}>{t('spaces.manageHint', '— You can manage spaces in this facility')}</span>
                                </div>
                            </div>
                        )}
                        {/* Filters */}
                        <div className="page-card mb-4">
                            <div style={{ padding: '14px 16px', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                                <div style={{ position: 'relative', flex: 1, minWidth: 220 }}>
                                    <MdSearch style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                    <input className="form-control" style={{ paddingLeft: 34 }} placeholder={t('spaces.searchPlaceholder', 'Search spaces...')}
                                        value={search} onChange={e => setSearch(e.target.value)} />
                                </div>
                                <select className="form-select" style={{ width: 180 }} value={filterBuilding} onChange={e => setFilterBuilding(e.target.value)}>
                                    <option value="">{t('spaces.allBuildings', 'All Buildings')}</option>
                                    {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                </select>
                                <select className="form-select" style={{ width: 140 }} value={filterStatus} onChange={e => setFilterStatus(e.target.value)}>
                                    <option value="">{t('spaces.allStatus', 'All Status')}</option>
                                    {SPACE_STATUS.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                                </select>
                            </div>
                        </div>

                        {/* Space Cards Grid */}
                        {loading ? (
                            <div className="spinner-wrap"><div className="spinner-border" /></div>
                        ) : (
                            <div className="row g-3">
                                {spaces.map(space => (
                                    <div key={space.id} className="col-xl-3 col-lg-4 col-md-6">
                                        <div style={{
                                            background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
                                            padding: 16, transition: 'box-shadow 0.2s',
                                            borderTop: `3px solid ${space.status === 'available' ? '#22c55e' : space.status === 'occupied' ? '#f59e0b' : '#ef4444'}`
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 10 }}>
                                                <div>
                                                    <span style={{ fontSize: 22 }}>{typeIcon(space.space_type)}</span>
                                                </div>
                                                <span className={`badge-status ${space.status}`}>{statusLabel(space.status)}</span>
                                            </div>
                                            <div style={{ fontWeight: 600, fontSize: 15, marginBottom: 2 }}>{space.name}</div>
                                            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>{space.building_name} · {t('spaces.floorLabel', 'Floor')} {space.floor || '—'} · #{space.room_number || '—'}</div>
                                            <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#475569', marginBottom: 10 }}>
                                                {space.capacity > 0 && <span><MdPeople size={14} style={{ marginRight: 2 }} />{space.capacity}</span>}
                                                {space.area && <span><MdSquareFoot size={14} style={{ marginRight: 2 }} />{space.area} m²</span>}
                                            </div>
                                            {space.department && <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 8 }}>{space.department}</div>}
                                            <div style={{ display: 'flex', gap: 6 }}>
                                                <button className="btn btn-sm btn-light flex-fill" onClick={() => openBooking(space)} style={{ fontSize: 12 }}>{t('spaces.btnBook', 'Book')}</button>
                                                {canManageSpace && (
                                                    <>
                                                        <button className="btn btn-sm btn-light" onClick={() => openEditSpace(space)}><MdEdit size={14} /></button>
                                                        <button className="btn btn-sm btn-light" style={{ color: '#ef4444' }} onClick={() => handleDeleteSpace(space.id)}><MdDelete size={14} /></button>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                                {!spaces.length && (
                                    <div className="col-12" style={{ textAlign: 'center', padding: 60, color: '#94a3b8' }}>
                                        <MdMeetingRoom size={48} style={{ marginBottom: 12, display: 'block', margin: '0 auto 12px' }} />
                                        {t('spaces.emptyState', 'No spaces found')}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )
            }

            {
                activeTab === 'buildings' && (
                    <div className="row g-3">
                        {buildings.map(b => {
                            const isMyFacility = isManager && assignedFacility?.id === b.id;
                            return (
                                <div key={b.id} className="col-lg-4 col-md-6">
                                    <div style={{
                                        background: '#fff', borderRadius: 12, padding: 20,
                                        border: isMyFacility ? '2px solid #0ea5e9' : '1px solid #e2e8f0',
                                        boxShadow: isMyFacility ? '0 0 0 3px rgba(14,165,233,0.12)' : 'none'
                                    }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                <div style={{ width: 44, height: 44, borderRadius: 10, background: isMyFacility ? '#e0f2fe' : '#f8fafc', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <MdBusiness size={24} style={{ color: isMyFacility ? '#0284c7' : '#94a3b8' }} />
                                                </div>
                                                <div>
                                                    <div style={{ fontWeight: 600, fontSize: 15 }}>{b.name}</div>
                                                    <div style={{ fontSize: 12, color: '#64748b' }}>{b.floors} {t('spaces.floorsCount', 'floor(s)')}</div>
                                                </div>
                                            </div>
                                            {isMyFacility && (
                                                <span style={{ fontSize: 11, background: '#0ea5e9', color: '#fff', padding: '3px 10px', borderRadius: 12, fontWeight: 600, whiteSpace: 'nowrap' }}>
                                                    {t('spaces.yourFacilityBadge', 'Your Facility')}
                                                </span>
                                            )}
                                        </div>
                                        {b.address && <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>{b.address}</div>}
                                        <div style={{ display: 'flex', gap: 12, fontSize: 13, marginBottom: 8 }}>
                                            {b.total_area && <div><strong>{b.total_area}</strong> m²</div>}
                                            <div><strong>{b.total_spaces}</strong> {t('spaces.spacesCountLabel', 'spaces')}</div>
                                        </div>
                                        {b.manager_name && (
                                            <div style={{ fontSize: 12, color: '#64748b', display: 'flex', alignItems: 'center', gap: 4 }}>
                                                <MdPeople size={14} /> {t('spaces.managerLabel', 'Manager:')} <strong>{b.manager_name}</strong>
                                            </div>
                                        )}
                                        {isMyFacility && (
                                            <button className="btn btn-primary btn-sm w-100 mt-3"
                                                onClick={() => { setFilterBuilding(String(b.id)); setActiveTab('spaces'); }}
                                                style={{ fontSize: 13 }}>
                                                {t('spaces.btnViewSpaces', 'View Spaces')}
                                            </button>
                                        )}
                                        {isAdmin && (
                                            <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                                                <button className="btn btn-sm btn-light flex-fill" onClick={() => openEditBuilding(b)}>
                                                    <MdEdit size={14} style={{ marginRight: 6 }} /> {t('spaces.btnEdit', 'Edit')}
                                                </button>
                                                <button className="btn btn-sm btn-light flex-fill" style={{ color: '#ef4444' }} onClick={() => handleDeleteBuilding(b.id)}>
                                                    <MdDelete size={14} style={{ marginRight: 6 }} /> {t('spaces.btnDelete', 'Delete')}
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )
            }

            {/* Space Modal */}
            {
                showSpaceModal && (
                    <Modal title={editSpaceId ? t('spaces.modalEditSpace', 'Edit Space') : t('spaces.modalAddSpace', 'Add New Space')} onClose={() => setShowSpaceModal(false)}>
                        <form onSubmit={handleSaveSpace}>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelBuilding', 'Building *')}</label>
                                    <select className="form-select" value={spaceForm.building_id} onChange={e => setSpaceForm({ ...spaceForm, building_id: e.target.value })} required>
                                        <option value="">{t('spaces.selectBuildingOption', 'Select building')}</option>
                                        {buildings.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelSpaceName', 'Space Name *')}</label>
                                    <input className="form-control" value={spaceForm.name} onChange={e => setSpaceForm({ ...spaceForm, name: e.target.value })} required />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelFloor', 'Floor')}</label>
                                    <input type="number" className="form-control" value={spaceForm.floor} onChange={e => setSpaceForm({ ...spaceForm, floor: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelRoomNumber', 'Room Number')}</label>
                                    <input className="form-control" value={spaceForm.room_number} onChange={e => setSpaceForm({ ...spaceForm, room_number: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelType', 'Type')}</label>
                                    <select className="form-select" value={spaceForm.space_type} onChange={e => setSpaceForm({ ...spaceForm, space_type: e.target.value })}>
                                        {SPACE_TYPES.map(st => <option key={st} value={st}>{typeLabel(st)}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelCapacity', 'Capacity')}</label>
                                    <input type="number" className="form-control" value={spaceForm.capacity} onChange={e => setSpaceForm({ ...spaceForm, capacity: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelArea', 'Area (m²)')}</label>
                                    <input type="number" className="form-control" step="0.1" value={spaceForm.area} onChange={e => setSpaceForm({ ...spaceForm, area: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelPricePerHour', 'Price per hour ({currency})', { currency })}</label>
                                    <input type="number" className="form-control" min="0" step="0.01" value={spaceForm.price_per_hour} onChange={e => setSpaceForm({ ...spaceForm, price_per_hour: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelPricePerDay', 'Price per day ({currency})', { currency })}</label>
                                    <input type="number" className="form-control" min="0" step="0.01" value={spaceForm.price_per_day || 0} onChange={e => setSpaceForm({ ...spaceForm, price_per_day: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelPricePerMonth', 'Price per month ({currency})', { currency })}</label>
                                    <input type="number" className="form-control" min="0" step="0.01" value={spaceForm.price_per_month || 0} onChange={e => setSpaceForm({ ...spaceForm, price_per_month: e.target.value })} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelStatus', 'Status')}</label>
                                    <select className="form-select" value={spaceForm.status} onChange={e => setSpaceForm({ ...spaceForm, status: e.target.value })}>
                                        {SPACE_STATUS.map(s => <option key={s} value={s}>{statusLabel(s)}</option>)}
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelDepartment', 'Department')}</label>
                                    <input className="form-control" value={spaceForm.department} onChange={e => setSpaceForm({ ...spaceForm, department: e.target.value })} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelAssignedTo', 'Assigned To')}</label>
                                    <select className="form-select" value={spaceForm.assigned_to} onChange={e => setSpaceForm({ ...spaceForm, assigned_to: e.target.value })}>
                                        <option value="">{t('spaces.noAssignmentOption', 'No assignment')}</option>
                                        {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                                    </select>
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelAmenities', 'Amenities')}</label>
                                    <input className="form-control" placeholder={t('spaces.amenitiesPlaceholder', 'e.g. Projector, Whiteboard, AC')} value={spaceForm.amenities} onChange={e => setSpaceForm({ ...spaceForm, amenities: e.target.value })} />
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                                <button type="button" className="btn btn-light" onClick={() => setShowSpaceModal(false)}>{t('spaces.btnCancel', 'Cancel')}</button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>
                                    {saving ? <><span className="spinner-border spinner-border-sm me-2" />{t('spaces.savingLabel', 'Saving...')}</> : t('spaces.btnSaveSpace', 'Save Space')}
                                </button>
                            </div>
                        </form>
                    </Modal>
                )
            }

            {/* Building Modal */}
            {
                showBuildingModal && (
                    <Modal title={editBuildingId ? t('spaces.modalEditFacility', 'Edit Facility') : t('spaces.modalAddFacility', 'Add New Facility')} onClose={() => setShowBuildingModal(false)}>
                        <form onSubmit={handleSaveBuilding}>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelFacilityName', 'Facility Name *')}</label>
                                    <input className="form-control" value={buildingForm.name} onChange={e => setBuildingForm({ ...buildingForm, name: e.target.value })} required />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelAddress', 'Location / Address')}</label>
                                    <textarea className="form-control" rows={2} value={buildingForm.address} onChange={e => setBuildingForm({ ...buildingForm, address: e.target.value })} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelFloorsCount', 'Number of Floors (optional)')}</label>
                                    <input type="number" className="form-control" value={buildingForm.floors} onChange={e => setBuildingForm({ ...buildingForm, floors: e.target.value })} />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelTotalArea', 'Total Area (m²)')}</label>
                                    <input type="number" className="form-control" step="0.1" value={buildingForm.total_area} onChange={e => setBuildingForm({ ...buildingForm, total_area: e.target.value })} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelFacilityManager', 'Facility Manager *')} <span style={{ color: '#ef4444' }}>{t('spaces.requiredHint', '(required)')}</span></label>
                                    <select className="form-select" value={buildingForm.manager_id} onChange={e => setBuildingForm({ ...buildingForm, manager_id: e.target.value })} required>
                                        <option value="">{t('spaces.selectManagerOption', 'Select a manager')}</option>
                                        {users.filter(u => u.role === 'manager' || u.role === 'admin').map(u => (
                                            <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                                        ))}
                                    </select>
                                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{t('spaces.managerHint', 'Only managers and admins can be assigned as facility managers.')}</div>
                                </div>
                            </div>
                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                                <button type="button" className="btn btn-light" onClick={() => setShowBuildingModal(false)}>{t('spaces.btnCancel', 'Cancel')}</button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? t('spaces.savingLabel', 'Saving...') : editBuildingId ? t('spaces.btnUpdateFacility', 'Update Facility') : t('spaces.btnSaveFacility', 'Save Facility')}</button>
                            </div>
                        </form>
                    </Modal>
                )
            }

            {/* Booking Modal */}
            {
                showBookingModal && (
                    <Modal title={t('spaces.modalBookSpace', 'Book a Space')} onClose={() => setShowBookingModal(false)}>
                        <form onSubmit={handleSaveBooking}>
                            <div className="row g-3">
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelMeetingTitle', 'Meeting Title')}</label>
                                    <input className="form-control" value={bookingForm.title} onChange={e => setBookingForm({ ...bookingForm, title: e.target.value })} placeholder={t('spaces.meetingTitlePlaceholder', 'e.g. Team Standup')} />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelOrderType', 'Order Type')}</label>
                                    <select className="form-select" value={bookingForm.order_type} onChange={e => setBookingForm({ ...bookingForm, order_type: e.target.value })}>
                                        <option value="booking">{t('spaces.orderTypeBooking', 'Short booking')}</option>
                                        <option value="rental">{t('spaces.orderTypeRental', 'Space rental')}</option>
                                    </select>
                                </div>
                                {bookingForm.order_type === 'rental' && (
                                    <div className="col-md-4">
                                        <label className="form-label fw-semibold">{t('spaces.labelRentalPeriod', 'Rental period')}</label>
                                        <select className="form-select" value={bookingForm.rental_months} onChange={e => setBookingForm({ ...bookingForm, rental_months: e.target.value })}>
                                            <option value="1">{t('spaces.rentalMonths1', '1 month')}</option><option value="3">{t('spaces.rentalMonths3', '3 months')}</option><option value="6">{t('spaces.rentalMonths6', '6 months')}</option><option value="12">{t('spaces.rentalMonths12', '12 months')}</option>
                                        </select>
                                    </div>
                                )}
                                <div className="col-md-4">
                                    <label className="form-label fw-semibold">{t('spaces.labelPriceUnit', 'Price unit')}</label>
                                    <select className="form-select" value={bookingForm.pricing_unit} onChange={e => setBookingForm({ ...bookingForm, pricing_unit: e.target.value })}>
                                        <option value="hour">{t('spaces.perHourOption', 'Per hour ·')} {RWF(bookingSpace?.price_per_hour || 0)}</option>
                                        <option value="day">{t('spaces.perDayOption', 'Per day ·')} {RWF(bookingSpace?.price_per_day || 0)}</option>
                                        <option value="month">{t('spaces.perMonthOption', 'Per month ·')} {RWF(bookingSpace?.price_per_month || 0)}</option>
                                    </select>
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelStartDateTime', 'Start Date & Time *')}</label>
                                    <input type="datetime-local" className="form-control" value={bookingForm.start_datetime} onChange={e => setBookingForm({ ...bookingForm, start_datetime: e.target.value })} required />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelEndDateTime', 'End Date & Time *')}</label>
                                    <input type="datetime-local" className="form-control" value={bookingForm.end_datetime} onChange={e => setBookingForm({ ...bookingForm, end_datetime: e.target.value })} required />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label fw-semibold">{t('spaces.labelAttendees', 'Attendees')}</label>
                                    <input type="number" className="form-control" value={bookingForm.attendees} onChange={e => setBookingForm({ ...bookingForm, attendees: e.target.value })} min={1} />
                                </div>
                                <div className="col-12">
                                    <label className="form-label fw-semibold">{t('spaces.labelPurpose', 'Purpose')}</label>
                                    <textarea className="form-control" rows={2} value={bookingForm.purpose} onChange={e => setBookingForm({ ...bookingForm, purpose: e.target.value })} />
                                </div>
                            </div>

                            {bookingTotal > 0 && (
                                <div style={{ background: '#fff7ed', borderRadius: 10, padding: '12px 16px', marginTop: 16, border: '1px solid #fed7aa' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <span style={{ fontSize: 13, color: '#92400e' }}>{translateUiText('Total')} ({bookingForm.pricing_unit === 'hour' ? `${bookingHours.toFixed(1)} ${translateUiText('hr')}` : bookingForm.pricing_unit === 'day' ? `${Math.max(1, (bookingHours / 24).toFixed(1))} ${translateUiText('day(s)')}` : `${bookingForm.rental_months} ${translateUiText('month(s)')}`} × {RWF(bookingUnitPrice)}/{bookingForm.pricing_unit})</span>
                                        <span style={{ fontSize: 16, fontWeight: 800, color: '#c2410c' }}>{RWF(bookingTotal)}</span>
                                    </div>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #fed7aa' }}>
                                        <span style={{ fontSize: 13, color: '#9a3412', fontWeight: 600 }}>{t('spaces.depositRequiredLabel', 'Deposit required to confirm interest ({pct}%)', { pct: bookingDepositPercent })}</span>
                                        <span style={{ fontSize: 15, fontWeight: 800, color: '#9a3412' }}>{RWF(bookingDepositAmount)}</span>
                                    </div>
                                </div>
                            )}

                            {bookingTotal > 0 && (
                                <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#9a3412', background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 12px', marginTop: 12 }}>
                                    <input type="checkbox" checked={depositAccepted} onChange={e => setDepositAccepted(e.target.checked)} required />
                                    <span>{t('spaces.depositAgreementText', "The booker agrees to pay a deposit of {amount} ({pct}% of the total) to confirm genuine interest before this booking is approved. Track the deposit and remaining balance via the booking's payment status.", { amount: RWF(bookingDepositAmount), pct: bookingDepositPercent })}</span>
                                </label>
                            )}

                            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
                                <button type="button" className="btn btn-light" onClick={() => setShowBookingModal(false)}>{t('spaces.btnCancel', 'Cancel')}</button>
                                <button type="submit" className="btn btn-primary" disabled={saving}>{t('spaces.btnConfirmBooking', 'Confirm Booking')}</button>
                            </div>
                        </form>
                    </Modal>
                )
            }
        </div >
    );
}
